/**
 * Print / Excel / PDF export icons — the "touch of a button" report actions.
 *
 * Robustness rules learned the hard way:
 *  - never depend on Content-Disposition being readable (it is hidden from JS
 *    on cross-origin responses unless the server exposes it), so the filename
 *    is derived locally and the header is only an override;
 *  - verify the payload actually looks like the format requested before
 *    handing it to the user — an error page saved as .xlsx is worse than a
 *    clear failure;
 *  - print opens a real document the browser can print rather than relying on
 *    a hidden iframe, which is blocked in several configurations;
 *  - surface the real HTTP status and server message on failure. "Report
 *    failed" with no detail is unusable for diagnosis.
 *  - when `getViewGrid` is supplied, Print/PDF/Excel all use that on-screen
 *    matrix (Print renders it locally; PDF sends it as viewGrid; Excel builds
 *    from the same columns/rows via exportXlsxLocal when provided).
 */
import { useState } from 'react'
import { FileSpreadsheet, FileText, Printer } from 'lucide-react'
import api from '@/services/api'
import { printViewGrid } from '@/components/shared/printViewGrid'

export interface ViewGridPayload {
  headers: string[]
  rows: string[][]
  title?: string
}

interface ExportIconsProps {
  template: string
  filters?: Record<string, unknown>
  formats?: Array<'pdf' | 'xlsx'>
  /** Base filename (no extension). Defaults to the template id. */
  filenameBase?: string
  /**
   * When provided, the on-screen column headers and display values drive
   * Print (local HTML table) and PDF (`filters.viewGrid`) so both match the
   * DealGrid — and Excel when `exportXlsxLocal` uses the same source.
   */
  getViewGrid?: () => ViewGridPayload
  /**
   * When provided, Excel is built locally from the on-screen grid instead of
   * the server Debt Pipeline workbook — same columns/rows/order as the DealGrid.
   */
  exportXlsxLocal?: () => Promise<void>
  /** The Print button's tooltip, when the default ("the on-screen view") is not
   *  what Print produces — e.g. a fixed server template. */
  printTitle?: string
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

function isoDate(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Read a Blob's leading bytes so we can confirm what the server really sent. */
async function magic(blob: Blob, n = 5): Promise<string> {
  const head = await blob.slice(0, n).arrayBuffer()
  return String.fromCharCode(...new Uint8Array(head))
}

/** Server error bodies arrive as a Blob when responseType is 'blob'. */
async function blobDetail(blob: unknown): Promise<string | null> {
  if (!(blob instanceof Blob)) return null
  try {
    const parsed = JSON.parse(await blob.text())
    return typeof parsed?.detail === 'string' ? parsed.detail : null
  } catch {
    return null
  }
}

export default function ExportIcons({
  template, filters = {}, formats = ['pdf', 'xlsx'], filenameBase, getViewGrid, exportXlsxLocal,
  printTitle,
}: ExportIconsProps) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const fetchReport = async (format: 'pdf' | 'xlsx') => {
    const grid = getViewGrid?.()
    const payloadFilters: Record<string, unknown> = { ...filters }
    if (grid && grid.headers.length > 0) {
      payloadFilters.viewGrid = { headers: grid.headers, rows: grid.rows }
      if (grid.title?.trim()) payloadFilters.viewName = grid.title.trim()
    }
    const res = await api.post(
      '/reports/generate',
      { template, filters: payloadFilters, format },
      { responseType: 'blob' },
    )
    const blob = res.data as Blob

    // Confirm the bytes match the requested format before saving them.
    const head = await magic(blob)
    if (format === 'pdf' && !head.startsWith('%PDF')) {
      throw new Error(`server returned ${blob.type || 'unknown content'} instead of a PDF`)
    }
    if (format === 'xlsx' && !head.startsWith('PK')) {
      throw new Error(`server returned ${blob.type || 'unknown content'} instead of a workbook`)
    }

    const disposition = String(res.headers?.['content-disposition'] ?? '')
    const fromHeader = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/)?.[1]
    const filename = fromHeader || `${filenameBase ?? template}_${isoDate()}.${format}`
    const typed = blob.type
      ? blob
      : new Blob([blob], { type: format === 'pdf' ? 'application/pdf' : XLSX_MIME })
    return { blob: typed, filename }
  }

  const save = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 30_000)
  }

  /** Print a PDF blob without a pop-up.
   *
   *  Previously this used window.open(url, '_blank', 'noopener,noreferrer').
   *  A window opened with `noopener` returns null to the opener by
   *  specification, so the null was misreported to the user as a blocked
   *  pop-up when nothing was blocking anything. A same-origin blob URL in an
   *  offscreen iframe prints with no pop-up involved; if the engine refuses
   *  to print a PDF that way, the file is saved so the action never dead-ends. */
  const print = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob)
    const frame = document.createElement('iframe')
    frame.setAttribute('aria-hidden', 'true')
    frame.style.position = 'fixed'
    frame.style.right = '0'
    frame.style.bottom = '0'
    frame.style.width = '1px'
    frame.style.height = '1px'
    frame.style.border = '0'
    frame.style.opacity = '0'
    frame.style.pointerEvents = 'none'

    let settled = false
    const release = () => {
      window.setTimeout(() => {
        frame.remove()
        URL.revokeObjectURL(url)
      }, 0)
    }

    const fallbackToDownload = () => {
      if (settled) return
      settled = true
      frame.remove()
      URL.revokeObjectURL(url)
      save(blob, filename)
      setDone(`Saved ${filename} — open it to print.`)
    }

    frame.onload = () => {
      const win = frame.contentWindow
      if (!win) {
        fallbackToDownload()
        return
      }
      try {
        win.focus()
        win.print()
        settled = true
        setDone('Print dialog opened')
        window.setTimeout(release, 60_000)
      } catch {
        fallbackToDownload()
      }
    }
    frame.onerror = fallbackToDownload
    // Backstop: some engines neither load nor error on a PDF in an iframe.
    window.setTimeout(fallbackToDownload, 4_000)

    document.body.appendChild(frame)
    frame.src = url
  }

  const run = async (action: 'print' | 'pdf' | 'xlsx') => {
    setBusy(action)
    setError(null)
    setDone(null)
    try {
      if (action === 'print' && getViewGrid) {
        const grid = getViewGrid()
        if (grid.headers.length > 0) {
          printViewGrid(grid)
          setDone('Print dialog opened for the on-screen view')
          return
        }
      }
      if (action === 'xlsx' && exportXlsxLocal) {
        await exportXlsxLocal()
        const name = `${filenameBase ?? template}_${isoDate()}.xlsx`
        setDone(`Saved ${name}`)
        return
      }
      const format = action === 'xlsx' ? 'xlsx' : 'pdf'
      const { blob, filename } = await fetchReport(format)
      if (action === 'print') print(blob, filename)
      else { save(blob, filename); setDone(`Saved ${filename}`) }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: unknown }; message?: string }
      const status = err?.response?.status
      const detail = await blobDetail(err?.response?.data)
      setError(
        status
          ? `Report failed (HTTP ${status})${detail ? `: ${detail}` : ''}`
          : `Report failed: ${err?.message ?? 'network error'}`,
      )
    } finally {
      setBusy(null)
    }
  }

  const buttonCls =
    'inline-flex h-7 w-7 items-center justify-center rounded border border-amber-400/15 ' +
    'text-stone-400 hover:text-amber-300 hover:border-amber-400/40 transition-colors ' +
    'disabled:opacity-40 disabled:cursor-wait'

  return (
    <div className="flex items-center gap-1.5" data-testid="export-icons">
      <button
        type="button"
        className={buttonCls}
        disabled={busy !== null}
        onClick={() => void run('print')}
        title={
          busy === 'print'
            ? 'Preparing print…'
            : printTitle ?? 'Print the on-screen pipeline view (same columns as PDF / Excel)'
        }
        aria-label="Print"
      >
        <Printer size={13} />
      </button>
      <button
        type="button"
        className={buttonCls}
        disabled={busy !== null}
        onClick={() => void run('pdf')}
        title={busy === 'pdf' ? 'Generating PDF…' : 'Download as PDF'}
        aria-label="Download PDF"
      >
        <FileText size={13} />
      </button>
      {formats.includes('xlsx') && (
        <button
          type="button"
          className={buttonCls}
          disabled={busy !== null}
          onClick={() => void run('xlsx')}
          title={busy === 'xlsx' ? 'Generating Excel…' : 'Download as Excel'}
          aria-label="Download Excel"
        >
          <FileSpreadsheet size={13} />
        </button>
      )}
      {error && <span className="text-[10px] text-red-400 max-w-[340px] truncate" title={error}>{error}</span>}
      {!error && done && <span className="text-[10px] text-emerald-400 truncate" title={done}>{done}</span>}
    </div>
  )
}
